import { useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { getAllCountries, COUNTRY_CONFIGS } from '../lib/country-config'
import CountrySelector from '../components/CountrySelector'
import { Tour } from '../components/Tour'

function validateCPF(raw: string): boolean {
  const d = raw.replace(/\D/g, '')
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false
  let s = 0
  for (let i = 0; i < 9; i++) s += +d[i] * (10 - i)
  let c1 = (s * 10) % 11; if (c1 >= 10) c1 = 0
  if (c1 !== +d[9]) return false
  s = 0
  for (let i = 0; i < 10; i++) s += +d[i] * (11 - i)
  let c2 = (s * 10) % 11; if (c2 >= 10) c2 = 0
  return c2 === +d[10]
}

function formatCPF(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`
}

function formatPhone(v: string): string {
  let p = v.replace(/[^\d+\s]/g, '')
  if (p && !p.startsWith('+')) p = '+' + p
  return p
}

function validatePhone(phone: string): boolean {
  if (!phone) return true
  return phone.replace(/\D/g, '').length >= 10
}

type SignerRole = 'SIGNER' | 'WITNESS'

interface SignerForm {
  nome: string
  email: string
  countryCode: string
  telefone: string
  nationalId: string
  role: SignerRole
  senderPays: boolean  // if true, the document sender pays for this signer
}

function emptySigner(role: SignerRole = 'SIGNER'): SignerForm {
  return { nome: '', email: '', countryCode: 'BR', telefone: '', nationalId: '', role, senderPays: false }
}

// Minimum witness fee per currency (covers Stripe minimum)
const WITNESS_FEE: Record<string, number> = {
  BRL: 0.50, USD: 0.10, EUR: 0.10, GBP: 0.10, CAD: 0.10, AUD: 0.10,
  NZD: 0.10, CHF: 0.10, SEK: 1.00, PLN: 0.50, RON: 0.50, COP: 500,
  MXN: 2.00, CLP: 100, ARS: 10, INR: 5, SGD: 0.10, MYR: 0.50,
  THB: 3, PHP: 5, NGN: 50, ZAR: 1, AED: 0.50, ILS: 0.50, JPY: 15,
}

const SENDER_PAYS_MARKUP = 0.20 // 20% markup when sender pays for a signer

export default function NewDocument() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const countries = getAllCountries()

  const [file, setFile] = useState<File | null>(null)
  const [docName, setDocName] = useState('')
  const [signers, setSigners] = useState<SignerForm[]>([emptySigner()])
  const submittingRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [selfieRequired, setSelfieRequired] = useState(false)
  const [createdDoc, setCreatedDoc] = useState<{ id: string; signers: Array<{ nome: string; email: string; telefone: string; tokenAcesso: string }> } | null>(null)
  const [emailsSent, setEmailsSent] = useState(false)
  const [sendingEmails, setSendingEmails] = useState(false)

  // Cost summary
  const costSummary = useMemo(() => {
    let signerPaysTotal = 0
    let senderPaysTotal = 0
    const items: Array<{ name: string; role: string; amount: number; currency: string; paidBy: string }> = []

    signers.forEach((s, i) => {
      const config = COUNTRY_CONFIGS[s.countryCode]
      if (!config) return

      const isWitness = s.role === 'WITNESS'
      const witnessFee = WITNESS_FEE[config.currency] || 0.10
      const fullFee = config.signaturePriceLocal

      if (isWitness) {
        // Witness: always pays minimum fee themselves
        signerPaysTotal += witnessFee
        items.push({
          name: s.nome || `${t('newDoc.roleWitness', 'Testemunha')} ${i + 1}`,
          role: t('newDoc.roleWitness', 'Testemunha'),
          amount: witnessFee,
          currency: config.currency,
          paidBy: t('newDoc.witnessPaysSelf', 'Paga taxa mínima (próprio)'),
        })
      } else if (s.senderPays) {
        // Signer covered by sender: signer pays minimum (for bank trail), sender pays the rest +20%
        const signerPays = witnessFee
        const senderPays = (fullFee - witnessFee) * (1 + SENDER_PAYS_MARKUP)
        signerPaysTotal += signerPays
        senderPaysTotal += senderPays

        items.push({
          name: s.nome || `${t('newDoc.roleSigner', 'Contratante')} ${i + 1}`,
          role: t('newDoc.roleSigner', 'Contratante'),
          amount: signerPays,
          currency: config.currency,
          paidBy: t('newDoc.paysMinimum', 'Paga taxa mínima (rastro bancário)'),
        })
        items.push({
          name: `↳ ${t('newDoc.senderCovers', 'Você cobre')} ${s.nome || `#${i + 1}`}`,
          role: '',
          amount: senderPays,
          currency: config.currency,
          paidBy: t('newDoc.paidBySender', 'Você paga (+20%)'),
        })
      } else {
        // Signer pays full amount themselves
        signerPaysTotal += fullFee
        items.push({
          name: s.nome || `${t('newDoc.roleSigner', 'Contratante')} ${i + 1}`,
          role: t('newDoc.roleSigner', 'Contratante'),
          amount: fullFee,
          currency: config.currency,
          paidBy: t('newDoc.paidBySelf', 'O próprio paga'),
        })
      }
    })

    return { items, senderPaysTotal, signerPaysTotal }
  }, [signers, t])

  function handleFileChange(selectedFile: File | null) {
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile)
      if (!docName) setDocName(selectedFile.name.replace(/\.pdf$/i, ''))
    } else if (selectedFile) {
      setError(t('newDoc.pdfRequired', 'Envie um arquivo PDF.'))
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragActive(false)
    handleFileChange(e.dataTransfer.files?.[0] || null)
  }

  function updateSigner(index: number, field: keyof SignerForm, value: string | boolean) {
    setSigners(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  function addSigner(role: SignerRole = 'SIGNER') {
    setSigners(prev => [...prev, emptySigner(role)])
  }

  function removeSigner(index: number) {
    if (signers.length <= 1) return
    setSigners(prev => prev.filter((_, i) => i !== index))
  }

  function fillAsSigner() {
    try {
      const stored = localStorage.getItem('pakt_user')
      if (!stored) return
      const user = JSON.parse(stored)
      setSigners(prev => {
        const newSigners = [...prev]
        const emptyIdx = newSigners.findIndex(s => !s.nome && !s.email)
        const filled: SignerForm = {
          nome: user.nome || '',
          email: user.email || '',
          countryCode: user.countryCode || 'BR',
          telefone: user.telefone || '',
          nationalId: user.cpf || '',
          role: 'SIGNER',
          senderPays: false,
        }
        if (emptyIdx >= 0) {
          newSigners[emptyIdx] = filled
        } else {
          newSigners.push(filled)
        }
        return newSigners
      })
    } catch {}
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submittingRef.current) return
    submittingRef.current = true
    setError('')

    if (!file) { setError(t('newDoc.pdfRequired', 'Envie um arquivo PDF.')); return }
    if (!docName.trim()) { setError(t('newDoc.nameRequired', 'Informe o nome do documento.')); return }
    if (signers.some(s => !s.nome.trim() || !s.email.trim())) {
      setError(t('newDoc.signersRequired', 'Preencha nome e e-mail de todos os participantes.'))
      return
    }
    const missingId = signers.find(s => {
      const cfg = COUNTRY_CONFIGS[s.countryCode]?.nationalId
      return cfg && !s.nationalId.trim()
    })
    if (missingId) {
      const cfg = COUNTRY_CONFIGS[missingId.countryCode]?.nationalId!
      setError(`Informe o ${cfg.fieldName} de ${missingId.nome || 'um dos signatários'}.`)
      return
    }

    const missingPhone = signers.find(s => !s.telefone.trim())
    if (missingPhone) {
      setError(`Informe o telefone de ${missingPhone.nome || 'um dos signatários'}.`)
      return
    }

    const invalidPhone = signers.find(s => s.telefone && !validatePhone(s.telefone))
    if (invalidPhone) {
      setError(`Telefone inválido para ${invalidPhone.nome || 'um dos signatários'}. Use o formato +55 77 991322975.`)
      return
    }

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('metadata', JSON.stringify({
        nome: docName.trim(),
        selfieRequired,
        signers: signers.map(s => ({
          nome: s.nome,
          email: s.email,
          countryCode: s.countryCode,
          telefone: s.telefone || undefined,
          nationalId: s.nationalId || undefined,
          signatureRole: s.role,
          senderPays: s.senderPays,
        })),
      }))

      const res = await api.upload<any>('/api/documents', formData)
      const doc = res.document || res
      navigate(`/documents/${doc.id}/edit`)
    } catch {
      setError(t('newDoc.createError', 'Erro ao criar documento. Tente novamente.'))
    } finally {
      setLoading(false)
      submittingRef.current = false
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="text-xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">
            &larr; {t('common.back', 'Voltar')}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('newDoc.title', 'Novo Documento')}</h1>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PDF Upload */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-tour="upload-section">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('document.upload', 'Enviar PDF')}</h2>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
              onDragLeave={() => setDragActive(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
                dragActive ? 'border-indigo-500 bg-indigo-50' :
                file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-indigo-400'
              }`}
            >
              <input ref={fileInputRef} type="file" accept="application/pdf"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)} className="hidden" />
              {file ? (
                <div>
                  <div className="text-3xl mb-2">&#x2705;</div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              ) : (
                <div>
                  <div className="text-3xl mb-2">&#x1F4C4;</div>
                  <p className="font-medium text-gray-900">{t('document.dragDrop', 'Arraste e solte seu PDF aqui')}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('newDoc.dropzoneAlt', 'ou clique para selecionar')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Document Details */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('newDoc.details', 'Detalhes')}</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('document.name', 'Nome do documento')}</label>
              <input type="text" required value={docName} onChange={(e) => setDocName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                placeholder={t('newDoc.namePlaceholder', 'Ex: Contrato de Prestação de Serviços')} />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={selfieRequired} onChange={(e) => setSelfieRequired(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <div>
                <span className="text-sm font-medium text-gray-700">{t('document.selfieRequired', 'Exigir selfie dos signatários')}</span>
                <p className="text-xs text-gray-500">{t('document.selfieRequiredDesc', 'Cada signatário precisará tirar uma selfie')}</p>
              </div>
            </label>
          </div>

          {/* Participants */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-tour="signers-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900">{t('newDoc.participants', 'Participantes')}</h2>
              <div className="flex gap-2">
                <button type="button" onClick={fillAsSigner}
                  className="px-3 py-1.5 text-xs bg-gray-50 text-gray-700 font-medium rounded-lg hover:bg-gray-100 border border-gray-200 transition flex items-center gap-1">
                  &#x1F464; {t('newDoc.iAmSigner', 'Eu sou signatário')}
                </button>
                <button type="button" onClick={() => addSigner('SIGNER')}
                  className="px-3 py-1.5 text-xs bg-indigo-50 text-indigo-600 font-medium rounded-lg hover:bg-indigo-100 transition">
                  + {t('newDoc.roleSigner', 'Contratante')}
                </button>
                <button type="button" onClick={() => addSigner('WITNESS')}
                  className="px-3 py-1.5 text-xs bg-amber-50 text-amber-700 font-medium rounded-lg hover:bg-amber-100 transition">
                  + {t('newDoc.roleWitness', 'Testemunha')}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              {t('newDoc.participantsHint', 'Contratantes pagam a taxa de verificação de identidade. Testemunhas pagam uma taxa simbólica mínima (sempre pelo próprio). Você pode assumir o pagamento de contratantes (acréscimo de 20%).')}
            </p>

            <div className="space-y-4">
              {signers.map((signer, index) => {
                const config = COUNTRY_CONFIGS[signer.countryCode]
                const isWitness = signer.role === 'WITNESS'
                const fee = config
                  ? (isWitness ? (WITNESS_FEE[config.currency] || 0.10) : config.signaturePriceLocal)
                  : 0

                return (
                  <div key={index} className={`border rounded-xl p-4 ${
                    isWitness ? 'bg-amber-50/50 border-amber-200' : 'bg-white border-gray-200'
                  }`}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          isWitness ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {isWitness ? t('newDoc.roleWitness', 'Testemunha') : t('newDoc.roleSigner', 'Contratante')}
                        </span>
                        {config && (
                          <span className="text-xs text-gray-400">
                            {config.currency} {fee.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {signers.length > 1 && (
                          <button type="button" onClick={() => removeSigner(index)}
                            className="text-xs text-red-500 hover:text-red-700">
                            {t('newDoc.remove', 'Remover')}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t('document.signerName', 'Nome')} *</label>
                        <input type="text" required value={signer.nome}
                          onChange={(e) => updateSigner(index, 'nome', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t('document.signerEmail', 'E-mail')} *</label>
                        <input type="email" required value={signer.email}
                          onChange={(e) => updateSigner(index, 'email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{t('document.signerCountry', 'País')} *</label>
                        <CountrySelector
                          value={signer.countryCode}
                          onChange={(code) => {
                            updateSigner(index, 'countryCode', code)
                            updateSigner(index, 'nationalId', '')
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          {t('document.signerPhone', 'Telefone')} <span className="text-red-400">*</span>
                        </label>
                        <input type="tel" required value={signer.telefone}
                          onChange={(e) => updateSigner(index, 'telefone', formatPhone(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white"
                          placeholder="+55 11 90000-0000" />
                      </div>
                      {(() => {
                        const nationalIdConfig = COUNTRY_CONFIGS[signer.countryCode]?.nationalId
                        if (!nationalIdConfig) return null
                        return (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              {nationalIdConfig.fieldName} <span className="text-red-400">*</span>
                            </label>
                            <input type="text" required value={signer.nationalId}
                              onChange={(e) => {
                                const v = nationalIdConfig.fieldName === 'CPF'
                                  ? formatCPF(e.target.value)
                                  : e.target.value
                                updateSigner(index, 'nationalId', v)
                              }}
                              placeholder={nationalIdConfig.fieldName === 'CPF' ? '000.000.000-00' : ''}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white" />
                          </div>
                        )
                      })()}
                    </div>

                    {/* Sender pays toggle — only for signers, not witnesses */}
                    {!isWitness && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={signer.senderPays}
                            onChange={(e) => updateSigner(index, 'senderPays', e.target.checked)}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                          <span className="text-xs text-gray-600">
                            {t('newDoc.senderPays', 'Eu cubro a taxa deste contratante')}
                            {signer.senderPays && config && (() => {
                              const witnessFee = WITNESS_FEE[config.currency] || 0.10
                              const senderAmount = (config.signaturePriceLocal - witnessFee) * (1 + SENDER_PAYS_MARKUP)
                              return (
                                <span className="text-green-600 font-medium ml-1">
                                  ({t('newDoc.signerPaysMin', 'Contratante paga')} {config.currency} {witnessFee.toFixed(2)} + {t('newDoc.youPay', 'você paga')} {config.currency} {senderAmount.toFixed(2)})
                                </span>
                              )
                            })()}
                          </span>
                        </label>
                      </div>
                    )}
                    {isWitness && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-amber-600">
                          {t('newDoc.witnessAlwaysPays', 'A testemunha sempre paga a taxa mínima para validar sua participação.')}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Cost Summary */}
          {costSummary.items.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('newDoc.costSummary', 'Resumo de custos')}</h2>
              <div className="space-y-2">
                {costSummary.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <span className="font-medium text-gray-900">{item.name}</span>
                      <span className="text-xs text-gray-400 ml-2">({item.role})</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">{item.currency} {item.amount.toFixed(2)}</span>
                      <p className="text-xs text-gray-400">{item.paidBy}</p>
                    </div>
                  </div>
                ))}
              </div>
              {costSummary.senderPaysTotal > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">{t('newDoc.youPay', 'Você pagará')}:</span>
                  <span className="text-lg font-bold text-green-600">
                    BRL {costSummary.senderPaysTotal.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Send Links — shown shadowed before creation, active after */}
          <div className={`bg-white rounded-xl border shadow-sm p-6 transition-all ${
            createdDoc ? 'border-green-200 bg-green-50/30' : 'border-gray-100 opacity-50 pointer-events-none'
          }`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                createdDoc ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {createdDoc ? '✓' : '2'}
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('newDoc.sendStep', 'Enviar links de assinatura')}
              </h2>
            </div>

            {!createdDoc && (
              <p className="text-sm text-gray-400 text-center py-4">
                {t('newDoc.sendAfterCreate', 'Crie o documento acima para enviar os links aos signatários.')}
              </p>
            )}

            {createdDoc && (
              <div className="space-y-4">
                {/* Bulk send buttons */}
                <div className="flex flex-wrap gap-2">
                  <button onClick={async () => {
                    setSendingEmails(true)
                    try {
                      // This also changes status from DRAFT → PENDING
                      await api.post(`/api/documents/${createdDoc.id}/send`)
                      setEmailsSent(true)
                    } catch {} finally { setSendingEmails(false) }
                  }} disabled={sendingEmails || emailsSent}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    {sendingEmails ? t('newDoc.sendingEmails', 'Enviando...') : emailsSent ? t('newDoc.emailsSent', 'E-mails enviados!') : t('newDoc.sendAllEmail', 'Enviar e-mail para todos')}
                  </button>
                  <button onClick={() => {
                    createdDoc.signers.forEach((s, i) => {
                      setTimeout(() => {
                        const url = `${window.location.origin}/sign/${s.tokenAcesso}`
                        const msg = encodeURIComponent(
                          `Olá ${s.nome},\n\nVocê tem um documento para assinar:\n📄 ${docName}\n\nAcesse: ${url}\n\nEnviado via PAKT`
                        )
                        const phone = s.telefone ? s.telefone.replace(/[^0-9+]/g, '').replace('+', '') : ''
                        window.open(phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank')
                      }, i * 500)
                    })
                  }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {t('newDoc.sendAllWhatsApp', 'Enviar WhatsApp para todos')}
                  </button>
                </div>

                {/* Individual signer links */}
                <div className="space-y-2">
                  {createdDoc.signers.map((s, i) => {
                    const url = `${window.location.origin}/sign/${s.tokenAcesso}`
                    return (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-white rounded-lg border border-gray-100">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{s.nome}</span>
                          <span className="text-xs text-gray-500 ml-2">{s.email}</span>
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => { navigator.clipboard.writeText(url) }}
                            className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                            {t('docStatus.copy', 'Copiar')}
                          </button>
                          <button onClick={() => {
                            const msg = encodeURIComponent(`Olá ${s.nome},\n\nAssine o documento "${docName}":\n${url}\n\nPAKT`)
                            const phone = s.telefone ? s.telefone.replace(/[^0-9+]/g, '').replace('+', '') : ''
                            window.open(phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank')
                          }}
                            className="px-2.5 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition">
                            WhatsApp
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Continue to document */}
                <div className="text-center pt-2">
                  <Link to={`/documents/${createdDoc.id}`}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                    {t('newDoc.viewDocument', 'Ver status do documento →')}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Submit — only show before creation */}
          {!createdDoc && (
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
              <Link to="/dashboard"
                className="w-full sm:w-auto text-center px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition">
                {t('common.cancel', 'Cancelar')}
              </Link>
              <button type="submit" disabled={loading} data-tour="submit-btn"
                className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                {loading ? t('document.creating', 'Criando...') : t('document.submit', 'Criar documento')}
              </button>
            </div>
          )}
        </form>
      </main>

      {/* Tour passo-a-passo do primeiro documento. Só aparece se o user
          chegou pelo tour do Dashboard (storage 'pakt_tour_first_doc_dashboard'
          já marcado) e ainda não completou ESTE tour. */}
      {localStorage.getItem('pakt_tour_first_doc_dashboard') === '1' &&
       !createdDoc && (
        <Tour
          storageKey="pakt_tour_first_doc_newdoc"
          steps={[
            {
              targetSelector: '[data-tour="upload-section"]',
              title: 'Suba o PDF',
              body: 'Arraste o contrato aqui ou clique pra escolher. Aceita PDF até 20MB no plano grátis.',
              placement: 'bottom',
            },
            {
              targetSelector: '[data-tour="signers-section"]',
              title: 'Adicione signatários',
              body: 'Nome + e-mail de cada pessoa que vai assinar. Cada um recebe um link único por e-mail. Pode adicionar testemunhas também.',
              placement: 'top',
            },
            {
              targetSelector: '[data-tour="submit-btn"]',
              title: 'Pronto pra enviar',
              body: 'Ao criar, geramos o link de cada signatário e disparamos os e-mails automaticamente. Você acompanha no dashboard.',
              placement: 'top',
              nextLabel: 'Entendi',
            },
          ]}
        />
      )}
    </div>
  )
}
